import { ShiftType } from './shiftType.model'

/**
 * A single work shift for one employee on one day.
 *
 * Times are minutes from midnight rather than Date objects: the roster only
 * ever deals in wall-clock time within a day, so this keeps overlap maths,
 * sorting and the hour-grid geometry to plain integer arithmetic, no timezone
 * surprises when the same shift is read in another locale.
 */
export interface Shift {
  /** Stable key for React and for the store's own lookups. */
  id: string
  /**
   * The backend's id, set once this shift has been persisted. Absent means the
   * shift exists only locally, so a save must create it rather than update it.
   * Kept separate from `id` so nothing has to guess an id's origin from its
   * shape.
   */
  remoteId?: string
  userId: string
  /** ISO calendar day, YYYY-MM-DD */
  date: string
  /** minutes from midnight, 0-1440 */
  startMinutes: number
  /** minutes from midnight, 0-1440; always greater than startMinutes */
  endMinutes: number
  /**
   * What this entry is: time on the clock or an absence. See
   * `shiftType.model.ts`.
   *
   * Optional on the way in, so a roster written before types existed, whether
   * from the API or out of the persisted store, still reads. Everything that
   * consumes it goes through `shiftTypeDefinition`, which treats absent as
   * `WORK`.
   */
  type?: ShiftType
  note?: string
}

/** Shape the shift modal hands back, no id yet when creating. */
export type ShiftDraft = Omit<Shift, 'id'> & { id?: string }

/** 'grid', employees × days roster; 'calendar'. Teams-style hour grid */
export type ScheduleView = 'grid' | 'calendar' | 'payroll'

/**
 * Which span the calendar view shows. A sub-switch rather than three more tabs:
 * day, week and month are the same view at different zoom levels, while Grid
 * and Payroll are different answers to different questions.
 */
export type CalendarMode = 'day' | 'week' | 'month'

/** A named start/end pair offered as a one-click preset in the shift modal. */
export interface ShiftTemplate {
  id: string
  labelKey: string
  startMinutes: number
  endMinutes: number
}

/** One roster row: an employee plus their shifts in the visible range. */
export interface RosterRow {
  userId: string
  name: string
  shiftsByDate: Record<string, Shift[]>
}

/**
 * Bumped to 2 when the shifts API went live. It is part of the store's
 * localStorage key, so raising it abandons the old bucket, which, for anyone
 * who opened the schedule while it was offline-only, holds a demo roster
 * seeded by `seedDemoShifts`. Left in place, the first save of a week the user
 * never visited would have pushed those invented shifts to the server as real
 * ones. The backend is the source of truth now, so nothing of value is lost.
 */
// v3: `savedSnapshotByWeek` holds a shifts-only serialization. The v2 bucket
// this branch has been writing recorded shifts AND the restaurant's colour map
// in the same string, so every one of its snapshots differs from the new basis,
// read under v3's rules each persisted week would look edited and be pushed
// on first load, which is the write storm this version exists to end. Its
// shifts all carry a remoteId and come back from the first GET, so abandoning
// the bucket costs nothing the server does not already hold.
export const SCHEDULE_SCHEMA_VERSION = 3
